<template>
    <div class="portfolio-container" v-if="portfolioFields.default_currency != ''">
        <div class="portfolio-title">
            <h2>{{$t('portfolio.title')}}</h2>
            <el-button class="addProp" @click="addProp">{{$t('portfolio.addProp')}}</el-button>
        </div>

        <ul class="section-summary">
            <li>
                <h4 class="title">{{$t('portfolio.total_value')}}</h4>
                <p class="value">
                    <span class="symbol">{{portfolioFields.default_currency.code}}</span>
                    {{portfolioFields.default_currency.symbol}}{{portfolioFields.total_portfolio.total | NumFormat}}
                </p>
                <p
                    class="compare_score"
                >{{portfolioFields.default_currency.symbol}}{{portfolioFields.total_equity | NumFormat}}</p>
                <p class="hint">{{$t('portfolio.total_equity')}}</p>
            </li>
            <li>
                <h4 class="title">{{$t('portfolio.total_performance')}}</h4>
                <p
                    class="value"
                >{{portfolioFields.total_properties_gain.total | NumFormatAndShowSymbol}}{{portfolioFields.default_currency.symbol}}{{portfolioFields.total_properties_gain.total | NumFormat}}</p>
                <p
                    class="compare_score"
                    :class="{'up': portfolioFields.percentage_total_properties_gain > 0, 'down': portfolioFields.percentage_total_properties_gain < 0}"
                >{{ portfolioFields.percentage_total_properties_gain | NumFormat}}%</p>
                <p class="hint">{{$t('portfolio.vs_purchase_price')}}</p>
            </li>
            <li>
                <h4 class="title">{{$t('portfolio.monthly_performance')}}</h4>
                <p class="value">
                    {{portfolioFields.total_properties_monthly_gain | NumFormatAndShowSymbol}}{{portfolioFields.default_currency.symbol}}
                    {{portfolioFields.total_properties_monthly_gain.total | NumFormat}}
                </p>
                <p
                    class="compare_score"
                    :class="{'up': portfolioFields.percentage_total_properties_monthly_gain > 0, 'down': portfolioFields.percentage_total_properties_monthly_gain < 0}"
                >{{ portfolioFields.percentage_total_properties_monthly_gain | NumFormat}}%</p>
                <p class="hint">{{$t('portfolio.vs_last_month')}}</p>
            </li>
            <li
                v-if="portfolioFields.percentage_total_purchase_yield > 0 || portfolioFields.percentage_total_gross_yield>0"
            >
                <h4
                    class="title"
                    v-if="portfolioFields.percentage_total_gross_yield > 0"
                >{{$t('portfolio.yield_current_val')}}</h4>
                <p
                    class="value"
                    v-if="portfolioFields.percentage_total_gross_yield > 0"
                >{{portfolioFields.percentage_total_gross_yield.toFixed(1)}}%</p>

                <h4
                    class="title"
                    v-if="portfolioFields.percentage_total_purchase_yield > 0"
                >{{$t('portfolio.yield_purchase_price')}}</h4>
                <p
                    class="value"
                    v-if="portfolioFields.percentage_total_purchase_yield > 0"
                >{{portfolioFields.percentage_total_purchase_yield.toFixed(1)}}%</p>
            </li>
        </ul>

        <div class="section-flex">
            <div class="section-chart" id="section-chart"></div>

            <div class="section-breakdown" id="section-breakdown"></div>
        </div>

        <div class="section-properties">
            <h3
                class="title"
            >{{$t('portfolio.properties')}} ({{portfolioFields.default_currency.code}})</h3>
            <el-table
                :data="tableData"
                style="width: 100%"
                max-height="550"
                :default-sort="{prop:'valuation', order:'descending'}"
                :empty-text="$t('cityRanking.noResult')"
                header-cell-class-name="header-row-style"
            >
                <el-table-column
                    sortable
                    :sort-method="sortAlphabetically"
                    min-width="150"
                    :label="$t('portfolio.name')"
                >
                    <template slot-scope="scope" prop="name">
                        <div class="property-cell" @click="linkToPID(scope.row)">
                            <p class="property-group">
                                <img :src="scope.row.country_flag_url" :alt="scope.row.country" />
                                <span class="project-name">{{scope.row.name}}</span>
                            </p>
                            <p class="country">{{scope.row.country}}</p>
                        </div>
                    </template>
                </el-table-column>
                <el-table-column
                    sortable
                    :sort-by="'valuation'"
                    prop="valuation"
                    :sort-orders="['descending', 'ascending']"
                    min-width="130"
                    :label="$t('portfolio.current_val')"
                    align="right"
                >
                    <template slot-scope="scope" prop="valuation">
                        <div
                            @click="linkToPID(scope.row)"
                        >{{portfolioFields.default_currency.symbol}}{{scope.row.valuation | NumFormat}}</div>
                    </template>
                </el-table-column>
                <el-table-column
                    sortable
                    :sort-by="'percentage_monthly_gain'"
                    min-width="130"
                    :label="$t('portfolio.monthly_gain')"
                    align="right"
                >
                    <template slot-scope="scope" prop="percentage_monthly_gain">
                        <div @click="linkToPID(scope.row)">
                            <span
                                class="direction-arrow"
                                :class="{'up': scope.row.percentage_monthly_gain > 0, 'down': scope.row.percentage_monthly_gain <0 }"
                            >{{scope.row.percentage_monthly_gain | NumFormat}}%</span>
                            <p
                                class="direction-value"
                            >{{portfolioFields.default_currency.symbol}}{{scope.row.total_monthly_gain | NumFormat}}</p>
                        </div>
                    </template>
                </el-table-column>
                <el-table-column
                    sortable
                    :sort-by="'percentage_total_gain'"
                    min-width="130"
                    :label="$t('portfolio.total_gain')"
                    align="right"
                >
                    <template slot-scope="scope" prop="percentage_total_gain">
                        <div @click="linkToPID(scope.row)">
                            <span
                                class="direction-arrow"
                                :class="{'up': scope.row.percentage_total_gain > 0, 'down': scope.row.percentage_total_gain <0 }"
                            >{{scope.row.percentage_total_gain | NumFormat}}%</span>
                            <p
                                class="direction-value"
                            >{{portfolioFields.default_currency.symbol}}{{scope.row.total_gain | NumFormat}}</p>
                        </div>
                    </template>
                </el-table-column>
                <el-table-column min-width="150" :label="$t('portfolio.advice')">
                    <template slot-scope="scope" prop="advice">
                        <el-button
                            type="text"
                            class="sold-btn"
                            size="small"
                            @click="linkToPID(scope.row)"
                            :class="{'red': scope.row.advice == 'Sell' || scope.row.advice == 'Strong Sell'}"
                        >{{transAdvice(scope.row.advice)}}</el-button>
                    </template>
                </el-table-column>
            </el-table>
        </div>
    </div>
</template>
<style lang="scss">
.portfolio-container {
    font-family: GothamBook;
    .portfolio-title {
        display: flex;
        align-items: center;
        h2 {
            font-size: 24px;
            line-height: 1.33;
            color: #2e384d;
            margin-right: 16px;
        }
        .addProp {
            height: 26px;
            line-height: 26px;
            border-radius: 2px;
            background-color: var(--windows-blue);
            font-size: 11px;
            border: 0;
            width: 106px;
            padding: 0;
            color: var(--white);
        }
    }

    .section-summary {
        margin: 20px 0;
        display: flex;
        flex-wrap: wrap;
        padding: 16px 13px;
        border-radius: 4px;
        box-shadow: 0 5px 10px -10px rgba(0, 0, 0, 0.2);
        border: solid 1px #e5edf3;
        background-color: #f2f7ff;
        font-family: GothamBook;
        overflow: hidden;
        li {
            width: 50%;
            &:not(:first-child) {
                border-left: 1px solid rgba(102, 102, 102, 0.3);
            }
            &:not(:first-child) {
                padding-left: 20px;
            }

            &:nth-child(3) {
                margin-top: 20px;
                padding: 0;
                border-left: 0;
            }

            &:nth-child(4) {
                margin-top: 20px;
                .title {
                    margin-bottom: 5px;
                }
                .value {
                    font-size: 16px;
                    line-height: 1;
                    letter-spacing: -0.8px;
                    color: #333333;
                    margin-bottom: 12px;
                }
            }
        }
        .title {
            font-family: GothamBook;
            font-size: 10px;
            color: #333333;
            margin-bottom: 12px;
        }
        .value {
            font-family: GothamBook;
            font-size: 16px;
            line-height: 0.97;
            letter-spacing: -1.1px;
            color: #333333;
            margin-bottom: 2px;
        }
        .symbol {
            font-family: GothamBook;
            font-size: 11px;
        }
        .compare_score {
            font-family: GothamBook;
            font-size: 11px;
            line-height: 1;
            letter-spacing: -0.4px;
            color: #447ef6;
            margin-bottom: 5px;
            &.up {
                position: relative;
                margin-left: 14px;
                color: #447ef6;
                &:before {
                    content: "";
                    width: 0px;
                    height: 0px;
                    position: absolute;
                    left: -13px;
                    top: 4px;
                    display: inline-block;
                    border: 3px solid transparent;
                    border-top: 3px solid #447ef6;
                    border-left: 3px solid #447ef6;
                    transform: rotate(45deg);
                }
            }
            &.down {
                position: relative;
                margin-left: 14px;
                color: #c3484d;
                &:before {
                    content: "";
                    width: 0px;
                    height: 0px;
                    position: absolute;
                    left: -13px;
                    top: 0px;
                    display: inline-block;
                    border: 3px solid transparent;
                    border-bottom: 3px solid #c3484d;
                    border-left: 3px solid #c3484d;
                    -webkit-transform: rotate(45deg);
                    transform: rotate(-45deg);
                }
            }
        }
        .hint {
            font-family: GothamBook;
            font-size: 10px;
            line-height: 1;
            color: var(--brownish-grey);
        }
    }

    .section-flex {
        display: flex;
        justify-content: space-between;
        flex-wrap: wrap;
        margin-bottom: 20px;
        .section-chart,
        .section-breakdown {
            border-radius: 6px;
            box-shadow: 0 5px 10px -10px rgba(0, 0, 0, 0.2);
            border: solid 1px #eaf0f7;
            background-color: var(--white);
        }
        .section-chart {
            width: 100%;
            height: 330px;
            margin-bottom: 20px;
        }
        .section-breakdown {
            width: 100%;
            height: 280px;
        }
    }

    .section-properties {
        border-radius: 4px;
        box-shadow: 0 5px 10px -10px rgba(0, 0, 0, 0.2);
        border: solid 1px #eaf0f7;
        background-color: var(--white);
        padding: 20px;
        .title {
            font-family: GothamMedium;
            font-size: 12px;
            font-weight: 500;
            letter-spacing: 1.1px;
            color: #333333;
            margin-bottom: 20px;
        }
    }
    .sold-btn {
        height: 27px;
        border-radius: 3px;
        background-color: rgba(59, 107, 191, 0.1);
        font-family: GothamMedium;
        font-size: 13px;
        font-weight: 500;
        width: auto;
        padding: 7px 10px;
        color: var(--windows-blue);
        &:hover {
            border-color: transparent;
            color: var(--windows-blue);
            background-color: rgba(59, 107, 191, 0.1);
        }
        &.red {
            background-color: #f8eaea;
            color: #bc3036;
            &:hover {
                background-color: #f8eaea;
                color: #bc3036;
            }
        }
    }
}

@media (min-width: 768px) {
    .portfolio-container {
        .section-summary {
            padding: 18px;
            .value {
                font-size: 19px;
            }
            .symbol {
                font-size: 11px;
            }
            .title {
                font-size: 9.9px;
                margin-bottom: 10.6px;
            }
            li {
                width: 100%;
                max-width: 134px;
                &:not(:first-child) {
                    padding-left: 19px;
                }
                // &:last-child {
                //     padding-left: 0;
                //     border-left: 0;
                //     margin-top: 32px;
                // }
                &:ntc-child(4) {
                    padding-left: 0;
                    border-left: 0;
                    margin-top: 32px;
                }
                &:nth-child(3) {
                    border-left: 1px solid rgba(102, 102, 102, 0.3);
                    padding-left: 19px;
                    margin-top: 0;
                }
            }
        }
        .section-flex {
            .section-breakdown {
                height: 330px;
            }
        }
    }
}

@media (min-width: 992px) {
    .portfolio-container {
        .section-summary {
            padding: 20px 26px;
            flex-wrap: nowrap;
            .title {
                font-size: 11px;
                margin-bottom: 12px;
            }
            .value {
                font-size: 22px;
                margin-bottom: 4px;
            }
            li {
                width: 100%;
                max-width: unset;
                // &:last-child {
                //     padding-left: 25px;
                //     border-left: 1px solid rgba(102, 102, 102, 0.3);
                //     margin-top: 0;
                // }
                &:nth-child(4) {
                    padding-left: 25px;
                    border-left: 1px solid rgba(102, 102, 102, 0.3);
                    margin-top: 0;
                }
            }
        }
        .section-flex {
            width: 100%;
            .section-chart {
                width: calc(50% - 10px);
                margin-bottom: 0;
            }
            .section-breakdown {
                width: calc(50% - 10px);
            }
        }
    }
}

.el-table .cell {
    word-break: break-word;
}

.el-table tbody .cell {
    font-size: 12px;
    font-family: GothamBook;
}
.el-table tbody .cell .property-cell .project-name {
    font-size: 12px;
    font-family: GothamBook;
    margin-left: 10px;
    line-height: 1.2;
    margin-bottom: 5px;
}
.el-table tbody .cell .property-cell .property-group {
    display: flex;
    align-items: center;
}

.el-table tbody .cell .property-cell .property-group img {
    width: 25px;
    height: 17px;
    border-radius: 2px;
    border: solid 0.8px #e4ecf3;
    object-fit: cover;
    overflow: hidden;
}

.el-table tbody .cell .property-cell .country {
    font-family: GothamBook;
    font-size: 11px;
    line-height: 1.2;
    margin-left: 34px;
    color: var(--brownish-grey);
}
.direction-arrow {
    font-family: GothamBook;
    font-size: 13px;
    position: relative;
    &.up {
        position: relative;
        margin-left: 14px;
        color: #447ef6;
        &:before {
            content: "";
            width: 0px;
            height: 0px;
            position: absolute;
            left: -13px;
            top: 6px;
            display: inline-block;
            border: 3px solid transparent;
            border-top: 3px solid #447ef6;
            border-left: 3px solid #447ef6;
            transform: rotate(45deg);
        }
    }
    &.down {
        position: relative;
        margin-left: 14px;
        color: #c3484d;
        &:before {
            content: "";
            width: 0px;
            height: 0px;
            position: absolute;
            left: -13px;
            top: 2px;
            display: inline-block;
            border: 3px solid transparent;
            border-bottom: 3px solid #c3484d;
            border-left: 3px solid #c3484d;
            -webkit-transform: rotate(45deg);
            transform: rotate(-45deg);
        }
    }
}
.direction-value {
    font-family: GothamBook;
    font-size: 13px;
    color: #333333;
    text-align: right;
}

.header-row-style {
    background-color: #f6f9fc !important;
    // height: 35px;
    padding: 0px 0 !important;
    // line-height: 35px;
    &.ascending {
        .sort-caret.ascending {
            border-bottom-color: var(--windows-blue);
            top: 8px;
        }
    }
    &.descending {
        .sort-caret.descending {
            border-top-color: var(--windows-blue);
            bottom: 8px;
        }
    }
    .cell {
        font-family: GothamMedium;
        font-size: 11px;
        font-weight: 500;
        color: var(--brownish-grey);
    }
    .sort-caret {
        border-width: 4px;
    }
    .sort-caret.ascending {
        top: 8px;
        border-bottom-color: var(--brownish-grey);
    }
    .sort-caret.descending {
        bottom: 8px;
        border-top-color: var(--brownish-grey);
    }
}
</style>
<script>
import { Message } from "element-ui";
import { Loading } from "element-ui";

let echarts = require("echarts/lib/echarts");
require("echarts/lib/chart/line");
require("echarts/lib/chart/pie");
require("echarts/lib/component/tooltip");
require("echarts/lib/component/title");

let lineChart, pieChart, loading;

export default {
    name: "portfolioContainer",
    props: ["requestHeaders", "requestParams", "portfolioResp"],
    data() {
        const $t = this.$t.bind(this);
        return {
            tableData: [],
            pieOptions: {},
            lineOptions: {},
            portfolioFields: {
                default_currency: ""
            }
        };
    },

    beforeMount() {
        this.loading();
        this.initialData();
    },

    activated() {
        this.initialData();
    },

    mounted() {},

    updated() {
        this.initialChart();
    },

    methods: {
        loading() {
            loading = this.$loading({
                lock: true,
                target: ".main-container",
                text: this.$t("searchResultPage.loading"),
                color: "#3b6bbf",
                background: "rgba(255, 255, 255, 0.8)"
            });
        },
        initialData() {
            let url = `${this.requestParams.ppbApi}api/v2/portfolio/${this.requestParams.userId}`;

            this.$http.get(url, this.requestHeaders).then(resp => {
                if (resp.status == 200) {
                    let result = resp.data;
                    this.portfolioFields = result;
                    this.global_portfolio_breakdown =
                        result.global_portfolio_breakdown;
                    this.tableData = result.properties;
                    let monthArr = [];
                    for (let i = 0; i < result.valuations_month.length; i++) {
                        if (i == 0) {
                            monthArr.push(result.valuations_month[i]);
                        } else {
                            monthArr.push(
                                result.valuations_month[i]
                                    .split(" ", 1)
                                    .toString()
                            );
                        }
                    }
                    this.valuations = {
                        month: monthArr,
                        total: result.valuations_total
                    };
                }
                this.$emit("loaded", true);
                loading.close();
            });
        },

        initialChart() {
            lineChart = echarts.init(document.getElementById("section-chart"));
            pieChart = echarts.init(
                document.getElementById("section-breakdown")
            );

            function NumFormat(val) {
                if (!val) return "-";
                val = Number.parseInt(val);
                val = Math.abs(val);
                return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
            }
            let portfolioFields = this.portfolioFields;

            this.lineOptions = {
                title: {
                    text: `${this.$t("portfolio.TOTAL_PORTFOLIO_VALUE")} (${
                        this.portfolioFields.default_currency.code
                    })`,
                    top: 20,
                    left: 16,
                    textStyle: {
                        color: "#333",
                        fontFamily: "GothamMedium",
                        fontSize: 12
                    }
                },

                tooltip: {
                    trigger: "axis",
                    formatter: function(params) {
                        return `${params[0].name} <br/> ${
                            portfolioFields.default_currency.symbol
                        } ${NumFormat(params[0].value)}`;
                    },
                    opacity: "0.9",
                    backgroundColor: "#485465",
                    textStyle: {
                        fontFamily: "GothamBook",
                        fontSize: "8.4",
                        color: "#fff"
                    }
                    // axisPointer: {
                    //     type: "cross",
                    //     label: {
                    //         backgroundColor: "#6a7985"
                    //     }
                    // }
                },

                // legend: {
                // data: ["Total Value"]
                // data: [
                //     {
                //         name: "Total Value",
                //         textStyle: {
                //             fontFamily: "GothamBook",
                //             fontSize: "10",
                //             color: "#333"
                //         }
                //     }
                // ]
                // top: "28",
                // right: "17"
                // },

                grid: {
                    left: "12",
                    right: "16",
                    bottom: "14",
                    containLabel: true
                },

                xAxis: [
                    {
                        axisLabel: {
                            fontFamily: "GothamBook",
                            fontSize: "10",
                            color: "#666"
                        },
                        axisLine: {
                            lineStyle: {
                                color: "#f5f7f8"
                            }
                        },
                        type: "category",
                        boundaryGap: false,
                        data: []
                    }
                ],
                yAxis: [
                    {
                        axisLabel: {
                            fontFamily: "GothamBook",
                            fontSize: "10",
                            color: "#666"
                        },
                        axisLine: {
                            lineStyle: {
                                color: "#f5f7f8"
                            }
                        },
                        splitLine: {
                            lineStyle: {
                                color: ["#f5f7f8"]
                            }
                        }
                    }
                ],

                series: [
                    {
                        name: this.$t("portfolio.total_value"),
                        type: "line",
                        itemStyle: {
                            normal: {
                                color: new echarts.graphic.LinearGradient(
                                    0,
                                    0,
                                    0,
                                    1,
                                    [
                                        {
                                            offset: 0,
                                            color: "rgba(67, 146, 241, 0.35)"
                                        },
                                        {
                                            offset: 1,
                                            color: "rgba(67, 146, 241, 0.05)"
                                        }
                                    ]
                                ),
                                lineStyle: {
                                    width: 3,
                                    type: "solid",
                                    color: "#0180ff"
                                }
                            }
                        },
                        areaStyle: { normal: {} },
                        data: []
                    }
                ],

                color: ["#4c8ef4"]
            };

            this.lineOptions.xAxis[0].data = this.valuations.month;
            this.lineOptions.series[0].data = this.valuations.total;

            this.pieOptions = {
                title: {
                    text: this.$t("portfolio.GLOBAL_PORTFOLIO_BREAKDOWN"),
                    left: "left",
                    top: 20,
                    left: 16,
                    textStyle: {
                        width: "auto",
                        color: "#333",
                        fontFamily: "GothamMedium",
                        fontSize: 12
                    }
                },

                tooltip: {
                    trigger: "item"
                },

                series: [
                    {
                        type: "pie",
                        radius: window.innerWidth <= 414 ? "50%" : "40%",
                        center: [
                            "50%",
                            window.innerWidth <= 414 ? "60%" : "50%"
                        ],
                        selectedMode: "single",
                        minAngle: 10,
                        minShowLabelAngle: "36",
                        data: [],
                        label: {
                            align: "left"
                        },
                        labelLine: {
                            lineStyle: {
                                color: "#8eb0f7"
                            },
                            smooth: 0.2
                        },
                        itemStyle: {
                            emphasis: {
                                shadowBlur: 10,
                                shadowOffsetX: 0,
                                shadowColor: "rgba(0, 0, 0, 0.5)"
                            }
                        }
                    }
                ],

                color: ["#447ef6", "#9fdbfc", "#052991", "#7076c8", "#8eb0f7"]
            };

            if (this.global_portfolio_breakdown.length >= 5) {
                this.global_portfolio_breakdown.length = 5;
            }

            this.global_portfolio_breakdown.forEach(it => {
                this.pieOptions.series[0].data.push({
                    name: it.country,
                    value: it.amount[1].total,

                    label: {
                        show: window.innerWidth <= 414 ? false : true,
                        color: "#666",
                        backgroundColor: "#f2f7ff",
                        formatter: `{countrySymbol|} {countryName|{b}} \n {per|{d}%}`,
                        borderColor: "#eaf0f7",
                        height: 26,
                        borderWidth: 0.8,
                        borderRadius: 2.3,
                        padding: [6, 7],
                        align: "left",
                        fontFamily: "GothamBook",
                        fontSize: 10,
                        rich: {
                            countrySymbol: {
                                borderRadius: 0.4,
                                backgroundColor: {
                                    image: it.flag_url
                                }
                            },
                            countryName: {
                                fontFamily: "GothamBook",
                                fontSize: window.innerWidth <= 414 ? 9 : 10,
                                color: "#666",
                                lineHeight: 15
                            },
                            price: {
                                fontFamily: "GothamBook",
                                fontSize: 10,
                                color: "#666"
                            },
                            per: {
                                color: "#fff",
                                fontFamily: "GothamBook",
                                padding: [1, 2],
                                fontSize: 10,
                                backgroundColor: "#447ef6",
                                padding: [2, 4],
                                borderRadius: 1
                            }
                        }
                    },
                    labelLine: {
                        show: window.innerWidth <= 414 ? false : true,
                        emphasis: {
                            show: window.innerWidth <= 414 ? false : true
                        },
                        lineStyle: {
                            color: "#8eb0f7"
                        },
                        smooth: 0.2
                    },
                    tooltip: {
                        formatter: function(params) {
                            return `${params.name} (${params.percent}%) <br/> ${
                                portfolioFields.default_currency.symbol
                            } ${NumFormat(params.value)}`;
                        },
                        textStyle: {
                            // height: 26,
                            borderRadius: 2.3,
                            align: "left",
                            fontFamily: "GothamBook",
                            fontSize: 9
                        }
                    }
                });
            });

            lineChart.setOption(this.lineOptions);
            pieChart.setOption(this.pieOptions);
        },

        linkToPID(data) {
            this.$emit("linkToPID", data);
        },

        transAdvice: function(val) {
            if (val == "Sell" || val == "Hold") {
                return this.$t(`portfolio.${val}`);
            } else if (val == "Strong Sell") {
                return this.$t("portfolio.Strong_Sell");
            } else if (val == "Strong Hold") {
                return this.$t("portfolio.Strong_Hold");
            } else if (val == "Not Calculated") {
                return this.$t("portfolio.Not_Calculated");
            }
        },

        sortAlphabetically(a, b) {
            return a.name.localeCompare(b.name);
        },

        addProp() {
            this.$emit("addProperty", "addProperty");
        }
    },

    filters: {
        NumFormat: function(val) {
            if (!val) return "-";
            val = Number.parseFloat(val).toFixed(1);
            val = Math.abs(val);
            return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        },

        NumFormatAndShowSymbol: function(val) {
            if (!val) return "";
            val = Number.parseInt(val);
            let symbol = "-";
            if (val > 0) {
                symbol = "+";
            }
            return symbol;
        }
    }
};
</script>




// WEBPACK FOOTER //
// src/components/account/portfolioContainer.vue